import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Modal,
  Text,
  Group,
  Stack,
  Badge,
  Box,
  Button,
  Divider,
  Tabs,
  Paper,
  Loader,
  SimpleGrid,
  ThemeIcon,
  Avatar,
  Center,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Star,
  Users,
  CheckCircle,
  XCircle,
  Calendar,
  Edit2,
  Save,
  X,
  Plus,
} from 'react-feather';
import {
  cohortCoachesApi,
  type CoachDetailForModal,
  type CoachPastCohort,
  type SlotAvailabilityMap,
  type InterestStatus,
  type SlotAvailabilityStatus,
  type SlotConflict,
} from '../../../../services/api/cohortCoaches';
import classes from './CoachDetailModal.module.css';
import { getInitialsFromParts } from '../../../../utils/avatarHelpers';
import { medleyEmailOf } from '../../../../utils/medleyEmail';
import {
  NO_MEDLEY_EMAIL_BADGE_STYLES,
  NO_MEDLEY_EMAIL_LABEL,
} from '../../../../components/coaches/noMedleyEmailBadge';
import { formatDate, formatDateOnly, formatDateOnlyRange, formatTimeString, formatDayOfWeek } from '../../../../utils/formatters';
import { COACH_STATUS_CONFIG, COACH_AVAILABILITY_CONFIG, getStatusLabel, getStatusColor } from '../../../../config/statusConfig';
import { MODALITY_PROFICIENCY_OPTIONS } from '../../../../config/coachConfig';
import { coachesApi } from '../../../../services/api/coaches';
import { CompensationTabContent } from './CompensationTabContent';
import { RichTextEditor } from '../../../../components/common/RichTextEditor/RichTextEditor';
import { RichTextContent } from '../../../../components/common/RichTextEditor/RichTextContent';
import type { CoachStatus } from '../../../../types';
import { InterestQuestionCard } from '../../../coach/availability/InterestQuestionCard';
import { DeclineConfirmationCard } from '../../../coach/availability/DeclineConfirmationCard';
import { TimeSlotAvailabilitySection } from '../../../coach/availability/TimeSlotAvailabilitySection';
import { GroupPreferencesSection } from '../../../coach/availability/GroupPreferencesSection';

interface CoachDetailModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  coachUserId: string | null;
  onUpdate?: () => void;
}

// Helper to format coach name
function getCoachName(firstName: string | null, lastName: string | null, email: string): string {
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || email;
}

// Helper to get availability badge config (wraps shared config with percent display)
function getAvailabilityBadgeConfig(
  availabilityStatus: string,
  availabilityPercent: number,
): { color: string; text: string; variant: 'outline' | 'light' | 'filled' } {
  const config = COACH_AVAILABILITY_CONFIG[availabilityStatus as keyof typeof COACH_AVAILABILITY_CONFIG];
  if (!config) return { color: 'blue', text: 'Not Requested', variant: 'light' };

  let text = config.label;
  if (availabilityStatus === 'available') text = '100% Available';
  if (availabilityStatus === 'partially_available') text = `${availabilityPercent}% Available`;

  return { color: config.color, text, variant: (config.variant as 'outline' | 'light' | 'filled') || 'light' };
}

// Helper to normalize slot status (database may use 'available' or 'full').
// MED-1318: a slot with no entry in slot_availability is one the coach did not
// answer (shown as NOT ANSWERED). It is not available. The schedule grid and availability_percent
// already treat it that way.
function normalizeSlotStatus(status: string | undefined): 'full' | 'partial' | 'unavailable' | 'unanswered' {
  if (!status) return 'unanswered';
  if (status === 'available' || status === 'full') return 'full';
  if (status === 'partial') return 'partial';
  return 'unavailable';
}

// Helper to get slot status class
function getSlotStatusClass(status: string | undefined): string {
  const normalized = normalizeSlotStatus(status);
  switch (normalized) {
    case 'full':
      return classes.timeSlotAvailable;
    case 'partial':
      return classes.timeSlotPartial;
    case 'unanswered':
      return classes.timeSlotUnanswered;
    case 'unavailable':
    default:
      return classes.timeSlotUnavailable;
  }
}

// Helper to get slot status badge
function getSlotStatusBadge(status: string | undefined): {
  color: string;
  text: string;
  variant: 'outline' | 'filled';
} {
  const normalized = normalizeSlotStatus(status);
  switch (normalized) {
    case 'full':
      return { color: 'green', text: 'AVAILABLE', variant: 'outline' };
    case 'partial':
      return { color: 'orange', text: 'PARTIAL', variant: 'outline' };
    case 'unanswered':
      // Style guide "Upcoming" status: #ECEAE6 background, #888 text, no border
      return { color: '#ECEAE6', text: 'NOT ANSWERED', variant: 'filled' };
    case 'unavailable':
    default:
      return { color: 'gray', text: 'UNAVAILABLE', variant: 'outline' };
  }
}

export function CoachDetailModal({
  opened,
  onClose,
  cohortId,
  coachUserId,
  onUpdate,
}: CoachDetailModalProps) {
  const [activeTab, setActiveTab] = useState<string | null>('availability');
  const [coachDetail, setCoachDetail] = useState<CoachDetailForModal | null>(null);
  const [pastCohorts, setPastCohorts] = useState<CoachPastCohort[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingPastCohorts, setLoadingPastCohorts] = useState(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isEditingModalities, setIsEditingModalities] = useState(false);
  const [editedModalities, setEditedModalities] = useState<string[]>([]);
  const [editedApprovals, setEditedApprovals] = useState<string[]>([]);

  // Availability edit mode state (MED-431)
  const [isEditingAvailability, setIsEditingAvailability] = useState(false);
  const [formInterestStatus, setFormInterestStatus] = useState<InterestStatus | null>(null);
  const [formSlotAvailability, setFormSlotAvailability] = useState<SlotAvailabilityMap>({});
  const [formMaxGroups, setFormMaxGroups] = useState<number | null>(null);
  const [formIsFlexibleGroups, setFormIsFlexibleGroups] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  // Load coach detail when modal opens
  const loadCoachDetail = useCallback(async () => {
    if (!coachUserId) return;
    setLoadingDetail(true);
    try {
      const detail = await cohortCoachesApi.getCoachDetail(cohortId, coachUserId);
      setCoachDetail(detail);
      setEditedNotes(detail.internal_notes || '');
    } catch (error) {
      console.error('Failed to load coach detail:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load coach details',
        color: 'red',
      });
    } finally {
      setLoadingDetail(false);
    }
  }, [cohortId, coachUserId]);

  // Load past cohorts when Past Cohorts tab is activated
  const loadPastCohorts = useCallback(async () => {
    if (!coachUserId) return;
    setLoadingPastCohorts(true);
    try {
      const cohorts = await cohortCoachesApi.getCoachPastCohorts(cohortId, coachUserId);
      setPastCohorts(cohorts);
    } catch (error) {
      console.error('Failed to load past cohorts:', error);
    } finally {
      setLoadingPastCohorts(false);
    }
  }, [cohortId, coachUserId]);

  // Load detail when modal opens
  useEffect(() => {
    if (opened && coachUserId) {
      loadCoachDetail();
      setActiveTab('availability'); // Default to Availability tab
      setPastCohorts([]);
      setIsEditingAvailability(false);
      setHasUnsavedChanges(false);
    }
  }, [opened, coachUserId, loadCoachDetail]);

  // Load past cohorts when tab is activated
  useEffect(() => {
    if (opened && activeTab === 'past-cohorts' && coachUserId && pastCohorts.length === 0) {
      loadPastCohorts();
    }
  }, [opened, activeTab, coachUserId, loadPastCohorts, pastCohorts.length]);

  const handleSaveNotes = async () => {
    if (!coachDetail) return;
    try {
      await coachesApi.update(coachDetail.coach_profile_id, { notes: editedNotes });
      setCoachDetail((prev) => (prev ? { ...prev, internal_notes: editedNotes } : null));
      setIsEditingNotes(false);
      notifications.show({
        title: 'Notes Saved',
        message: 'Internal notes have been updated',
        color: 'green',
      });
      onUpdate?.();
    } catch (error) {
      console.error('Failed to save notes:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save internal notes',
        color: 'red',
      });
    }
  };

  const handleSaveModalities = async () => {
    if (!coachDetail) return;
    try {
      await coachesApi.update(coachDetail.coach_profile_id, {
        modality_proficiencies: editedModalities,
        modality_approvals: editedApprovals,
      });
      setCoachDetail((prev) => (prev ? { ...prev, modality_proficiencies: editedModalities, modality_approvals: editedApprovals } : null));
      setIsEditingModalities(false);
      notifications.show({
        title: 'Modalities Saved',
        message: 'Modality proficiencies and approvals have been updated',
        color: 'green',
      });
      onUpdate?.();
    } catch (error) {
      console.error('Failed to save modalities:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save modality proficiencies',
        color: 'red',
      });
    }
  };

  // ---- Availability Edit Handlers (MED-431) ----

  const handleStartEditing = () => {
    const ar = coachDetail?.availability_response;
    if (ar && ar.interest_status) {
      // Pre-populate from existing response
      setFormInterestStatus(ar.interest_status);
      setFormSlotAvailability(ar.slot_availability || {});
      setFormMaxGroups(ar.max_groups);
      setFormIsFlexibleGroups(ar.is_flexible_groups || false);
    } else {
      // Empty defaults for new availability
      setFormInterestStatus(null);
      setFormSlotAvailability({});
      setFormMaxGroups(null);
      setFormIsFlexibleGroups(false);
    }
    setHasUnsavedChanges(false);
    setIsEditingAvailability(true);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
    } else {
      setIsEditingAvailability(false);
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    setIsEditingAvailability(false);
    setHasUnsavedChanges(false);
  };

  const handleSubmitAvailability = async () => {
    if (!coachUserId || !formInterestStatus) return;
    setIsSubmitting(true);
    try {
      const payload = {
        interest_status: formInterestStatus,
        ...(formInterestStatus === 'interested' && {
          slot_availability: formSlotAvailability,
          max_groups: formIsFlexibleGroups ? undefined : (formMaxGroups ?? undefined),
          is_flexible_groups: formIsFlexibleGroups,
        }),
      };
      const updatedDetail = await cohortCoachesApi.submitAvailabilityResponse(
        cohortId,
        coachUserId,
        payload,
      );
      setCoachDetail(updatedDetail);
      setIsEditingAvailability(false);
      setHasUnsavedChanges(false);
      notifications.show({
        title: 'Success',
        message: availabilityResponse?.responded_at
          ? 'Coach availability updated'
          : 'Coach availability added',
        color: 'green',
      });
      onUpdate?.();
    } catch (error) {
      console.error('Failed to submit availability:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to save coach availability',
        color: 'red',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Track unsaved changes when form values change
  const handleInterestChange = (status: InterestStatus) => {
    setFormInterestStatus(status);
    setHasUnsavedChanges(true);
  };

  const handleSlotChange = (slotId: string, status: SlotAvailabilityStatus, conflicts?: SlotConflict[]) => {
    setFormSlotAvailability(prev => ({
      ...prev,
      [slotId]: { status, conflicts },
    }));
    setHasUnsavedChanges(true);
  };

  const handleMaxGroupsChange = (value: number | null) => {
    setFormMaxGroups(value);
    setHasUnsavedChanges(true);
  };

  const handleFlexibleGroupsChange = (value: boolean) => {
    setFormIsFlexibleGroups(value);
    setHasUnsavedChanges(true);
  };

  // Form validation (same logic as coach page)
  const isFormValid = useMemo(() => {
    if (!formInterestStatus) return false;
    if (formInterestStatus === 'not_interested') return true;
    // Interested: need at least one available slot + group preference
    if (Object.keys(formSlotAvailability).length > 0) {
      const hasAvailable = Object.values(formSlotAvailability).some(
        s => s.status === 'full' || s.status === 'partial',
      );
      if (!hasAvailable) return false;
    }
    if (!formMaxGroups && !formIsFlexibleGroups) return false;
    return true;
  }, [formInterestStatus, formSlotAvailability, formMaxGroups, formIsFlexibleGroups]);

  if (!coachUserId) return null;

  const availabilityResponse = coachDetail?.availability_response;

  // Build attribution text for view mode
  const attributionText = (() => {
    if (!availabilityResponse) return null;
    const { entered_by_name, entered_at, responded_at } = availabilityResponse;
    if (!entered_by_name) return null;

    if (responded_at && entered_at && new Date(entered_at).getTime() > new Date(responded_at).getTime()) {
      // CM updated after coach responded
      return `Responded ${formatDate(responded_at)} · Updated by ${entered_by_name} on ${formatDate(entered_at)}`;
    }
    // CM entered (no prior coach response, or CM was first)
    return `Entered by ${entered_by_name} on ${formatDate(entered_at)}`;
  })();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      padding={0}
      radius="md"
      withCloseButton={false}
      styles={{ body: { padding: 0 } }}
    >
      {loadingDetail ? (
        <Center py="xl" mih={400}>
          <Loader />
        </Center>
      ) : coachDetail ? (
        <>
          {/* Header */}
          <Box className={classes.header}>
            <Group justify="space-between" align="flex-start">
              <Group gap="sm">
                <Avatar
                  src={coachDetail.avatar_url}
                  size={56}
                  radius="xl"
                  color="pink"
                >
                  {getInitialsFromParts(coachDetail.first_name, coachDetail.last_name, coachDetail.email)}
                </Avatar>
                <Box>
                  <Text fw={600} size="lg">
                    {getCoachName(coachDetail.first_name, coachDetail.last_name, coachDetail.email)}
                  </Text>
                  {/* MED-1355: a non-Medley users.email is not the Medley
                      email. Show the badge, as on the coach profile. */}
                  {medleyEmailOf(coachDetail.email) ? (
                    <Text size="sm" c="dimmed">
                      {coachDetail.email}
                    </Text>
                  ) : (
                    <Badge
                      styles={NO_MEDLEY_EMAIL_BADGE_STYLES}
                      title="Needs a Medley email before they can lead a group or be a 1:1 coach"
                    >
                      {NO_MEDLEY_EMAIL_LABEL}
                    </Badge>
                  )}
                  <Group gap="xs" mt="xs">
                    <Badge
                      color={getStatusColor(COACH_STATUS_CONFIG, coachDetail.status as CoachStatus)}
                      variant="filled"
                      size="sm"
                      leftSection={
                        <Box
                          component="span"
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: 'currentColor',
                          }}
                        />
                      }
                    >
                      {getStatusLabel(COACH_STATUS_CONFIG, coachDetail.status as CoachStatus)}
                    </Badge>
                    {(() => {
                      const badge = getAvailabilityBadgeConfig(
                        coachDetail.availability_status,
                        coachDetail.availability_percent,
                      );
                      return (
                        <Badge color={badge.color} variant={badge.variant} size="sm">
                          {badge.text}
                        </Badge>
                      );
                    })()}
                  </Group>
                </Box>
              </Group>
              <Button
                variant="subtle"
                color="gray"
                size="compact-md"
                onClick={onClose}
                style={{ fontSize: 24, lineHeight: 1, padding: '4px 8px' }}
              >
                ×
              </Button>
            </Group>
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List px="lg" className={classes.tabList}>
              <Tabs.Tab value="profile">Profile</Tabs.Tab>
              <Tabs.Tab value="availability">Availability</Tabs.Tab>
              {/* MED-673: Partner coaches are not compensated through Medley. */}
              {coachDetail.coach_type !== 'partner' && (
                <Tabs.Tab value="compensation">Compensation</Tabs.Tab>
              )}
              <Tabs.Tab value="past-cohorts">Past Cohorts</Tabs.Tab>
            </Tabs.List>

            {/* Profile Tab */}
            <Tabs.Panel value="profile">
              <Box className={classes.tabContent}>
                <Stack gap="md">
                  {/* CONTACT INFORMATION */}
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                      Contact Information
                    </Text>
                    <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                      <Box>
                        <Group gap="xs" mb={4}>
                          <Mail size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="xs" c="dimmed">Medley Email</Text>
                        </Group>
                        {medleyEmailOf(coachDetail.email) ? (
                          <Text size="sm" fw={500}>{coachDetail.email}</Text>
                        ) : (
                          <Text size="sm" c="dimmed">Not set</Text>
                        )}
                      </Box>
                      <Box>
                        <Group gap="xs" mb={4}>
                          <Phone size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="xs" c="dimmed">Phone</Text>
                        </Group>
                        <Text size="sm" fw={500}>{coachDetail.phone || '-'}</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>

                  <Divider />

                  {/* COACHING SPECIALIZATIONS */}
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                      Coaching Specializations
                    </Text>
                    <Text size="sm">{coachDetail.coaching_specializations || 'No specializations listed'}</Text>
                  </Box>

                  <Divider />

                  {/* MODALITY PROFICIENCIES */}
                  <Box>
                    <Group justify="space-between" mb="xs">
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                        Modality Proficiencies
                      </Text>
                      {isEditingModalities ? (
                        <Group gap="xs">
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            leftSection={<X size={12} />}
                            onClick={() => {
                              setIsEditingModalities(false);
                              setEditedModalities(coachDetail.modality_proficiencies || []);
                              setEditedApprovals(coachDetail.modality_approvals || []);
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="light"
                            color="medleyGreen"
                            leftSection={<Save size={12} />}
                            onClick={handleSaveModalities}
                          >
                            Save
                          </Button>
                        </Group>
                      ) : (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<Edit2 size={12} />}
                          onClick={() => {
                            setEditedModalities(coachDetail.modality_proficiencies || []);
                            setEditedApprovals(coachDetail.modality_approvals || []);
                            setIsEditingModalities(true);
                          }}
                        >
                          Edit
                        </Button>
                      )}
                    </Group>
                    {isEditingModalities ? (
                      <Stack gap="xs">
                        {MODALITY_PROFICIENCY_OPTIONS.map((option) => {
                          const isProficient = editedModalities.includes(option.value);
                          const isApproved = editedApprovals.includes(option.value);
                          return (
                            <Group key={option.value} gap="xs">
                              <Badge
                                size="sm"
                                variant={isProficient ? 'filled' : 'outline'}
                                color="medleyGreen"
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setEditedModalities((prev) =>
                                    isProficient
                                      ? prev.filter((v) => v !== option.value)
                                      : [...prev, option.value],
                                  );
                                }}
                              >
                                {option.label}
                              </Badge>
                              <Badge
                                size="sm"
                                variant={isApproved ? 'filled' : 'outline'}
                                color={isApproved ? 'medleyGreen' : 'gray'}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  setEditedApprovals((prev) =>
                                    isApproved
                                      ? prev.filter((v) => v !== option.value)
                                      : [...prev, option.value],
                                  );
                                }}
                              >
                                {isApproved ? 'Approved' : 'Not Approved'}
                              </Badge>
                            </Group>
                          );
                        })}
                        <Text size="xs" c="dimmed" fs="italic">
                          Approval selections are internal to Medley and not visible to coaches.
                        </Text>
                      </Stack>
                    ) : coachDetail.modality_proficiencies?.length ? (
                      <Group gap="xs">
                        {coachDetail.modality_proficiencies.map((m) => {
                          const isApproved = (coachDetail.modality_approvals || []).includes(m);
                          return (
                            <Tooltip
                              key={m}
                              label={isApproved ? 'Medley Approved' : 'Not Approved'}
                              withArrow
                            >
                              <Badge
                                size="sm"
                                variant="light"
                                color="medleyGreen"
                                leftSection={isApproved ? <CheckCircle size={10} /> : undefined}
                              >
                                {MODALITY_PROFICIENCY_OPTIONS.find((o) => o.value === m)?.label || m}
                              </Badge>
                            </Tooltip>
                          );
                        })}
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">None assigned</Text>
                    )}
                  </Box>

                  <Divider />

                  {/* LOCATION & TIMEZONE */}
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                      Location & Timezone
                    </Text>
                    <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                      <Box>
                        <Group gap="xs" mb={4}>
                          <MapPin size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="xs" c="dimmed">Location</Text>
                        </Group>
                        <Text size="sm" fw={500}>{coachDetail.location || '-'}</Text>
                      </Box>
                      <Box>
                        <Group gap="xs" mb={4}>
                          <Clock size={14} color="var(--mantine-color-dimmed)" />
                          <Text size="xs" c="dimmed">Home-base Timezone</Text>
                        </Group>
                        <Text size="sm" fw={500}>{coachDetail.home_base_timezone || '-'}</Text>
                      </Box>
                      {coachDetail.timezone_preference && (
                        <Box>
                          <Text size="xs" c="dimmed" mb={4}>Current Timezone Preference</Text>
                          <Text size="sm" fw={500}>{coachDetail.timezone_preference}</Text>
                        </Box>
                      )}
                    </SimpleGrid>
                  </Box>

                  <Divider />

                  {/* PERFORMANCE */}
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                      Performance
                    </Text>
                    <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                      {/* Satisfaction Rating Card */}
                      <Paper className={classes.performanceCard}>
                        <Text size="xs" c="dimmed" mb="xs">Satisfaction Rating</Text>
                        <Group gap="xs" align="center">
                          <Text size="xl" fw={700}>
                            {coachDetail.satisfaction_rating?.toFixed(1) || '-'}
                          </Text>
                          {coachDetail.satisfaction_rating && (
                            <Group gap={2}>
                              {[1, 2, 3, 4, 5].map((i) => (
                                <Star
                                  key={i}
                                  size={16}
                                  fill={i <= Math.round(coachDetail.satisfaction_rating!) ? 'var(--mantine-color-yellow-5)' : 'none'}
                                  color="var(--mantine-color-yellow-5)"
                                />
                              ))}
                            </Group>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed" mt={4}>
                          Based on {coachDetail.total_reviews} review{coachDetail.total_reviews !== 1 ? 's' : ''}
                        </Text>
                      </Paper>

                      {/* Sessions This Cohort Card */}
                      <Paper className={classes.performanceCard}>
                        <Text size="xs" c="dimmed" mb="xs">Sessions This Cohort</Text>
                        <Group gap="xl">
                          <Box ta="center">
                            <Text size="lg" fw={700}>{coachDetail.session_counts.group}</Text>
                            <Text size="xs" c="dimmed">Group</Text>
                          </Box>
                          <Box ta="center">
                            <Text size="lg" fw={700}>{coachDetail.session_counts.one_on_one}</Text>
                            <Text size="xs" c="dimmed">1:1</Text>
                          </Box>
                          <Box ta="center">
                            <Text size="lg" fw={700}>{coachDetail.session_counts.workshop}</Text>
                            <Text size="xs" c="dimmed">Workshop</Text>
                          </Box>
                        </Group>
                      </Paper>
                    </SimpleGrid>
                  </Box>

                  <Divider />

                  {/* CURRENT ASSIGNMENTS */}
                  <Box>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                      Current Assignments
                    </Text>
                    {coachDetail.group_assignments.length === 0 ? (
                      <Text size="sm" c="dimmed">No groups assigned yet</Text>
                    ) : (
                      <Stack gap="xs">
                        {coachDetail.group_assignments.map((group) => (
                          <Group key={group.id} gap="sm">
                            <ThemeIcon size="sm" color="medleyGreen" variant="light">
                              <Users size={12} />
                            </ThemeIcon>
                            <Text size="sm">{group.name}</Text>
                            <Text size="xs" c="dimmed">({group.participant_count} participants)</Text>
                          </Group>
                        ))}
                      </Stack>
                    )}
                  </Box>

                  <Divider />

                  {/* INTERNAL MEDLEY NOTES */}
                  <Box>
                    <Group justify="space-between" mb="xs">
                      <Stack gap={2}>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                          Internal Medley Notes
                        </Text>
                        <Text size="xs" c="dimmed" fs="italic">
                          Notes and modality approval selections are not visible to coaches.
                        </Text>
                      </Stack>
                      {isEditingNotes ? (
                        <Group gap="xs">
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            leftSection={<X size={12} />}
                            onClick={() => {
                              setIsEditingNotes(false);
                              setEditedNotes(coachDetail.internal_notes || '');
                            }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="compact-xs"
                            variant="light"
                            color="medleyGreen"
                            leftSection={<Save size={12} />}
                            onClick={handleSaveNotes}
                          >
                            Save
                          </Button>
                        </Group>
                      ) : (
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          leftSection={<Edit2 size={12} />}
                          onClick={() => setIsEditingNotes(true)}
                        >
                          Edit
                        </Button>
                      )}
                    </Group>
                    {isEditingNotes ? (
                      <RichTextEditor
                        value={editedNotes}
                        onChange={setEditedNotes}
                        placeholder="Add internal notes about this coach..."
                        minHeight={120}
                      />
                    ) : (
                      <RichTextContent
                        content={coachDetail.internal_notes}
                        emptyText="No internal notes"
                      />
                    )}
                  </Box>
                </Stack>
              </Box>
            </Tabs.Panel>

            {/* Availability Tab */}
            <Tabs.Panel value="availability">
              <Box className={classes.tabContent}>
                {isEditingAvailability ? (
                  /* ---- EDIT MODE (MED-431) ---- */
                  <Stack gap="lg">
                    {/* Request Details Section */}
                    {availabilityResponse && (
                      <Box>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                          Request Details
                        </Text>
                        <SimpleGrid cols={2} spacing="sm" verticalSpacing="xs">
                          {availabilityResponse.partner_name && (
                            <Box>
                              <Text size="xs" c="dimmed">Partner</Text>
                              <Text size="sm" fw={500}>{availabilityResponse.partner_name}</Text>
                            </Box>
                          )}
                          {availabilityResponse.program_name && (
                            <Box>
                              <Text size="xs" c="dimmed">Program</Text>
                              <Text size="sm" fw={500}>{availabilityResponse.program_name}</Text>
                            </Box>
                          )}
                          <Box>
                            <Text size="xs" c="dimmed">Sessions</Text>
                            <Text size="sm" fw={500}>{availabilityResponse.session_count} sessions</Text>
                          </Box>
                          <Box>
                            <Text size="xs" c="dimmed">Cohort Dates</Text>
                            <Text size="sm" fw={500}>
                              {formatDateOnlyRange(availabilityResponse.cohort_start_date, availabilityResponse.cohort_end_date)}
                            </Text>
                          </Box>
                          {availabilityResponse.modalities?.length > 0 && (
                            <Box>
                              <Text size="xs" c="dimmed">Modalities</Text>
                              <Text size="sm" fw={500}>{availabilityResponse.modalities.join(', ')}</Text>
                            </Box>
                          )}
                          {availabilityResponse.compensation_text && (
                            <Box>
                              <Text size="xs" c="dimmed">Compensation</Text>
                              <Text size="sm" fw={500}>{availabilityResponse.compensation_text}</Text>
                            </Box>
                          )}
                        </SimpleGrid>
                      </Box>
                    )}

                    <Divider />

                    {/* Interest Selection */}
                    <InterestQuestionCard
                      value={formInterestStatus}
                      onChange={handleInterestChange}
                      perspective="cm"
                    />

                    {/* Decline Confirmation */}
                    {formInterestStatus === 'not_interested' && (
                      <DeclineConfirmationCard perspective="cm" />
                    )}

                    {/* Time Slot Availability (if interested) */}
                    {formInterestStatus === 'interested' && availabilityResponse && (
                      <TimeSlotAvailabilitySection
                        timeSlots={availabilityResponse.time_slots}
                        slotAvailability={formSlotAvailability}
                        onChange={handleSlotChange}
                        perspective="cm"
                        durationMinutes={availabilityResponse.session_duration_minutes}
                      />
                    )}

                    {/* Group Capacity (if interested) */}
                    {formInterestStatus === 'interested' && availabilityResponse && (
                      <GroupPreferencesSection
                        maxGroups={formMaxGroups}
                        isFlexibleGroups={formIsFlexibleGroups}
                        sessionCount={availabilityResponse.session_count}
                        additionalNotes=""
                        onMaxGroupsChange={handleMaxGroupsChange}
                        onFlexibleGroupsChange={handleFlexibleGroupsChange}
                        onNotesChange={() => {}}
                        perspective="cm"
                      />
                    )}
                  </Stack>
                ) : (
                  /* ---- VIEW MODE ---- */
                  <>
                    {/* State: Pending Response */}
                    {coachDetail.availability_status === 'pending_response' && (
                      <Center py="xl">
                        <Stack align="center" gap="md">
                          <ThemeIcon size={64} radius="xl" color="gray" variant="light">
                            <Clock size={32} />
                          </ThemeIcon>
                          <Text fw={600} size="lg">Response Pending</Text>
                          <Text size="sm" c="dimmed" ta="center">
                            Waiting for this coach to submit their availability.
                          </Text>
                          <Button
                            variant="default"
                            leftSection={<Plus size={16} />}
                            onClick={handleStartEditing}
                          >
                            Add Availability
                          </Button>
                        </Stack>
                      </Center>
                    )}

                    {/* State: Not Requested */}
                    {coachDetail.availability_status === 'not_requested' && (
                      <Center py="xl">
                        <Stack align="center" gap="md">
                          <ThemeIcon size={64} radius="xl" color="gray" variant="light">
                            <Clock size={32} />
                          </ThemeIcon>
                          <Text fw={600} size="lg">No Request Sent</Text>
                          <Text size="sm" c="dimmed" ta="center">
                            An availability request has not been sent to this coach yet.
                          </Text>
                        </Stack>
                      </Center>
                    )}

                    {/* State: Unavailable (Declined) */}
                    {coachDetail.availability_status === 'unavailable' && availabilityResponse && (
                      <Stack gap="lg">
                        {/* Response Status Callout - Red */}
                        <Box className={`${classes.responseCallout} ${classes.responseCalloutDeclined}`}>
                          <ThemeIcon size="md" color="red" variant="light" radius="xl">
                            <XCircle size={16} />
                          </ThemeIcon>
                          <Box style={{ flex: 1 }}>
                            <Text fw={600} c="red">Not Available</Text>
                            <Text size="sm" c="dimmed">
                              Responded {formatDate(availabilityResponse.responded_at)}
                            </Text>
                            {attributionText && (
                              <Text size="xs" c="dimmed">{attributionText}</Text>
                            )}
                          </Box>
                          <Button
                            variant="default"
                            size="compact-sm"
                            leftSection={<Edit2 size={14} />}
                            onClick={handleStartEditing}
                          >
                            Edit Availability
                          </Button>
                        </Box>
                      </Stack>
                    )}

                    {/* State: Available or Partially Available (Interested) */}
                    {(coachDetail.availability_status === 'available' ||
                      coachDetail.availability_status === 'partially_available') &&
                      availabilityResponse && (
                        <Stack gap="lg">
                          {/* Response Status Callout - Green */}
                          <Box className={`${classes.responseCallout} ${classes.responseCalloutInterested}`}>
                            <ThemeIcon size="md" color="green" variant="light" radius="xl">
                              <CheckCircle size={16} />
                            </ThemeIcon>
                            <Box style={{ flex: 1 }}>
                              <Text fw={600} c="green">Interested</Text>
                              <Text size="sm" c="dimmed">
                                Responded {formatDate(availabilityResponse.responded_at)}
                              </Text>
                              {attributionText && (
                                <Text size="xs" c="dimmed">{attributionText}</Text>
                              )}
                            </Box>
                            <Button
                              variant="default"
                              size="compact-sm"
                              leftSection={<Edit2 size={14} />}
                              onClick={handleStartEditing}
                            >
                              Edit Availability
                            </Button>
                          </Box>

                          {/* GROUP CAPACITY */}
                          {(availabilityResponse.max_groups || availabilityResponse.is_flexible_groups) && (
                            <Box>
                              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                                Group Capacity
                              </Text>
                              <Group gap="md">
                                <Badge
                                  size="xl"
                                  variant="filled"
                                  color="medleyGreen"
                                  style={{ fontSize: '1.25rem', padding: '0.75rem 1rem' }}
                                >
                                  {availabilityResponse.is_flexible_groups ? '∞' : availabilityResponse.max_groups}
                                </Badge>
                                <Box>
                                  <Text size="sm" fw={500}>
                                    {availabilityResponse.is_flexible_groups
                                      ? 'As many as needed'
                                      : `Up to ${availabilityResponse.max_groups} group${availabilityResponse.max_groups !== 1 ? 's' : ''}`}
                                  </Text>
                                  <Text size="xs" c="dimmed">
                                    {availabilityResponse.is_flexible_groups
                                      ? 'Based on program needs'
                                      : `${availabilityResponse.session_count * (availabilityResponse.max_groups ?? 0)} total sessions`}
                                  </Text>
                                </Box>
                              </Group>
                            </Box>
                          )}

                          <Divider />

                          {/* TIME SLOT AVAILABILITY */}
                          <Box>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                              Time Slot Availability
                            </Text>
                            <Stack gap="xs">
                              {availabilityResponse.time_slots.map((slot) => {
                                const slotAvail = availabilityResponse.slot_availability?.[slot.id];
                                const rawStatus = slotAvail?.status;
                                const normalizedStatus = normalizeSlotStatus(rawStatus);
                                const statusBadge = getSlotStatusBadge(rawStatus);
                                const conflicts = slotAvail?.conflicts || [];

                                return (
                                  <Box key={slot.id}>
                                    <Box
                                      className={`${classes.timeSlotCard} ${getSlotStatusClass(rawStatus)}`}
                                    >
                                      <Group justify="space-between" align="flex-start">
                                        <Box>
                                          <Text fw={600} size="sm">
                                            {formatDayOfWeek(slot.day_of_week)} — {formatTimeString(slot.time)}
                                          </Text>
                                          <Text size="xs" c="dimmed">
                                            {slot.timezone}
                                          </Text>
                                        </Box>
                                        <Badge
                                          color={statusBadge.color}
                                          variant={statusBadge.variant}
                                          autoContrast={false}
                                          c={statusBadge.variant === 'filled' ? '#888888' : undefined}
                                          size="sm"
                                        >
                                          {statusBadge.text}
                                        </Badge>
                                      </Group>
                                    </Box>
                                    {/* Conflict Dates for PARTIAL slots */}
                                    {normalizedStatus === 'partial' && conflicts.length > 0 && (
                                      <Box pl="md" pt="xs">
                                        <Text size="xs" c="dimmed" mb={4}>Conflict Dates:</Text>
                                        {conflicts.map((conflict, idx) => (
                                          <Box key={idx} className={classes.conflictDate}>
                                            <Calendar size={12} />
                                            <Text size="xs">
                                              {formatDate(conflict.date)}
                                              {conflict.reason && ` — ${conflict.reason}`}
                                            </Text>
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>

                          {/* ADDITIONAL NOTES */}
                          {availabilityResponse.additional_notes && (
                            <>
                              <Divider />
                              <Box>
                                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">
                                  Additional Notes
                                </Text>
                                <Box className={classes.notesBlock}>
                                  <Text size="sm">{availabilityResponse.additional_notes}</Text>
                                </Box>
                              </Box>
                            </>
                          )}
                        </Stack>
                      )}
                  </>
                )}
              </Box>
            </Tabs.Panel>

            {/* Compensation Tab (E13-4 AC16) — hidden for partner coaches (MED-673) */}
            {coachDetail.coach_type !== 'partner' && (
              <Tabs.Panel value="compensation">
                <Box className={classes.tabContent}>
                  {coachUserId && activeTab === 'compensation' && (
                    <CompensationTabContent
                      cohortId={cohortId}
                      coachUserId={coachUserId}
                      onUpdated={onUpdate}
                    />
                  )}
                </Box>
              </Tabs.Panel>
            )}

            {/* Past Cohorts Tab */}
            <Tabs.Panel value="past-cohorts">
              <Box className={classes.tabContent}>
                <Text
                  size="xs"
                  fw={600}
                  c="dimmed"
                  mb="md"
                  style={{ letterSpacing: '0.5px', textTransform: 'uppercase' }}
                >
                  Cohort History
                </Text>

                {loadingPastCohorts ? (
                  <Center py="xl">
                    <Loader size="sm" />
                  </Center>
                ) : pastCohorts.length === 0 ? (
                  <Paper withBorder p="xl" ta="center">
                    <ThemeIcon size={48} radius={48} color="gray" variant="light" mx="auto" mb="md">
                      <Clock size={24} />
                    </ThemeIcon>
                    <Text size="sm" c="dimmed">No past cohorts</Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      This coach has not been part of any other cohorts.
                    </Text>
                  </Paper>
                ) : (
                  <Stack gap="md">
                    {pastCohorts.map((cohort) => (
                      <Box key={cohort.cohort_id} className={classes.pastCohortCard}>
                        <Group justify="space-between" align="flex-start">
                          <Box style={{ flex: 1 }}>
                            <Text fw={600} size="sm">{cohort.cohort_name}</Text>
                            <Text size="xs" c="dimmed">{cohort.partner_name || 'Unknown Partner'}</Text>
                            <Group gap="xs" mt="xs">
                              <Text size="xs" c="dimmed">
                                {formatDateOnly(cohort.start_date)} - {formatDateOnly(cohort.end_date)}
                              </Text>
                              <Text size="xs" c="dimmed">•</Text>
                              <Text size="xs" c="dimmed">
                                {cohort.group_count} group{cohort.group_count !== 1 ? 's' : ''}
                              </Text>
                              <Text size="xs" c="dimmed">•</Text>
                              <Text size="xs" c="dimmed">
                                {cohort.total_sessions} session{cohort.total_sessions !== 1 ? 's' : ''}
                              </Text>
                            </Group>
                          </Box>
                          {cohort.satisfaction_rating && (
                            <Group gap={4}>
                              <Star size={14} fill="var(--mantine-color-yellow-5)" color="var(--mantine-color-yellow-5)" />
                              <Text size="sm" fw={500}>{cohort.satisfaction_rating.toFixed(1)}</Text>
                            </Group>
                          )}
                        </Group>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Tabs.Panel>
          </Tabs>

          {/* Footer */}
          <Box className={classes.footer}>
            {isEditingAvailability ? (
              <Group justify="space-between">
                <Button variant="default" onClick={handleCancelEdit}>
                  Cancel
                </Button>
                <Button
                  color="medleyGreen"
                  loading={isSubmitting}
                  disabled={!isFormValid}
                  onClick={handleSubmitAvailability}
                >
                  Submit Response
                </Button>
              </Group>
            ) : (
              <Group justify="flex-end">
                <Button variant="default" onClick={onClose}>
                  Close
                </Button>
              </Group>
            )}
          </Box>

          {/* Discard changes confirmation */}
          <Modal
            opened={showDiscardConfirm}
            onClose={() => setShowDiscardConfirm(false)}
            title="Unsaved Changes"
            size="sm"
            centered
          >
            <Text size="sm" mb="lg">
              You have unsaved changes. Are you sure you want to discard them?
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setShowDiscardConfirm(false)}>
                Keep Editing
              </Button>
              <Button color="red" onClick={confirmDiscard}>
                Discard
              </Button>
            </Group>
          </Modal>
        </>
      ) : (
        <Center py="xl" mih={400}>
          <Text c="dimmed">Failed to load coach details</Text>
        </Center>
      )}
    </Modal>
  );
}

export default CoachDetailModal;
